import React, { useState, useEffect } from 'react';
import { 
Box, 
TextField, 
FormControlLabel, 
Checkbox, 
IconButton, 
Typography,
Tooltip,
Button,
FormControl,
InputLabel,
Select,
MenuItem,
Chip,
Accordion,
AccordionSummary,
AccordionDetails
} from '@mui/material';
import { Add, Delete, DragHandle, ExpandMore } from '@mui/icons-material';
import { ProgrammeAPI } from '../../utilities/ProgrammeAPI';


export function FieldEditor({ field, onUpdate, onRemove, listeners, allFields = [], allSections = [] }) {
    const [options, setOptions] = useState(field.options || ['']);
    const [groupName, setGroupName] = useState(field.groupName || '');
    const [label, setLabel] = useState(field.label || `${field.type} Field`);
    const [skipLogic, setSkipLogic] = useState(field.skipLogic || []);
    const [availableFaculties, setAvailableFaculties] = useState([]);
    const [availableLevels, setAvailableLevels] = useState([]);

    useEffect(() => {
        const fetchProgrammeMetadata = async () => {
            try {
                const response = await ProgrammeAPI.getAllProgrammes(); // Or a new endpoint for metadata
                if (response.status === 'success' && response.data) {
                    const faculties = new Set();
                    const levels = new Set();
                    response.data.forEach(programme => {
                        try {
                            const structure = JSON.parse(programme.programmeStructureJson || '{}');
                            if (structure.faculties) {
                                structure.faculties.forEach(f => {
                                    faculties.add(f.name);
                                    if (f.departments) {
                                        f.departments.forEach(d => {
                                            if (d.levels) {
                                                d.levels.forEach(l => {
                                                    levels.add(l.name);
                                                });
                                            }
                                        });
                                    }
                                });
                            }
                        } catch (e) {
                            console.error("Error parsing programme structure JSON:", e);
                        }
                    });
                    setAvailableFaculties(Array.from(faculties).sort());
                    setAvailableLevels(Array.from(levels).sort());
                }
            } catch (error) {
                console.error("Failed to fetch programme metadata:", error);
            }
        };

        if (field.type === 'programme-selector') {
            fetchProgrammeMetadata();
        }
    }, [field.type]);


    // Get available target fields for skip logic (fields that come after this field)
    const getAvailableTargetFields = () => {
        const currentFieldIndex = allFields.findIndex(f => f.id === field.id);
        if (currentFieldIndex === -1) return [];
        
        const subsequentFields = allFields.slice(currentFieldIndex + 1);
        const subsequentSections = allSections || [];
        
        return [
            ...subsequentFields.map(f => ({ type: 'field', id: f.id, label: f.label, target: f.id })),
            ...subsequentSections.map(s => ({ type: 'section', id: s.id, label: s.name, target: s.id }))
        ];
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };


    // For radio/select options
    const handleOptionChange = (index, value) => {
        const newOptions = [...options];
        newOptions[index] = value;
        setOptions(newOptions);
        onUpdate(field.id, { options: newOptions });
    };

    const addOption = () => {
        setOptions([...options, '']);
    };

    const removeOption = (index) => {
        if (options.length <= 1) return;
        const newOptions = options.filter((_, i) => i !== index);
        setOptions(newOptions);
    };

    const handleLabelChange = (e) => {
        setLabel(e.target.value);
        onUpdate(field.id, { label: e.target.value });
    };

    // Add condition to the delete button:
    {!field.locked && (
        <IconButton onClick={() => onRemove(field.id)}>
            <Delete />
        </IconButton>
    )}


    const handleGroupNameChange = (e) => {
        const newGroupName = e.target.value;
        setGroupName(newGroupName);
        onUpdate(field.id, { 
            ...field,  // Include existing field properties
            groupName: newGroupName 
        });
    };

    // For required checkbox
    const handleRequiredChange = (e) => {
        onUpdate(field.id, { required: e.target.checked });
    };

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove(field.id);
    };

    // Skip logic handlers
    const handleSkipLogicChange = (index, property, value) => {
        const newSkipLogic = [...skipLogic];
        newSkipLogic[index] = { ...newSkipLogic[index], [property]: value };
        setSkipLogic(newSkipLogic);
        onUpdate(field.id, { skipLogic: newSkipLogic });
    };

    const addSkipLogicRule = () => {
        const newRule = {
            id: `skip_${Date.now()}`,
            condition: 'equals',
            value: '',
            action: 'hide',
            target: '',
            targetType: 'field'
        };
        const newSkipLogic = [...skipLogic, newRule];
        setSkipLogic(newSkipLogic);
        onUpdate(field.id, { skipLogic: newSkipLogic });
    };

    const removeSkipLogicRule = (index) => {
        const newSkipLogic = skipLogic.filter((_, i) => i !== index);
        setSkipLogic(newSkipLogic);
        onUpdate(field.id, { skipLogic: newSkipLogic });
    };

    const renderFieldSpecificContent = () => {
        switch(field.type) {
        case 'select':
        case 'radio':
            return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                Options:
                </Typography>
                {options.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                    value={option}
                    onChange={(e) => {
                        handleOptionChange(index, e.target.value);
                        onUpdate(field.id, { options: [...options.slice(0, index), e.target.value, ...options.slice(index + 1)] });
                    }}
                    fullWidth
                    size="small"
                    variant="outlined"
                    />
                    {options.length > 1 && (
                    <Tooltip title="Remove option">
                        <IconButton 
                        onClick={() => {
                            removeOption(index);
                            onUpdate(field.id, { options: options.filter((_, i) => i !== index) });
                        }} 
                        size="small"
                        sx={{ ml: 1 }}
                        >
                        <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    )}
                </Box>
                ))}
                <Button 
                onClick={() => {
                    addOption();
                    onUpdate(field.id, { options: [...options, ''] });
                }} 
                size="small" 
                startIcon={<Add fontSize="small" />}
                variant="outlined"
                sx={{ mt: 1 }}
                >
                Add Option
                </Button>                {/* Skip Logic Configuration */}
                <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            Skip Logic {skipLogic.length > 0 && <Chip size="small" label={skipLogic.length} />}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Configure conditions to show/hide fields or sections based on this field's value.
                        </Typography>
                        
                        {skipLogic.map((rule, index) => (
                            <Box key={rule.id} sx={{ 
                                border: '1px solid #e0e0e0', 
                                borderRadius: 1, 
                                p: 2, 
                                mb: 2,
                                backgroundColor: '#f9f9f9'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Typography variant="body2">When this field</Typography>
                                    
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <Select
                                            value={rule.condition}
                                            onChange={(e) => handleSkipLogicChange(index, 'condition', e.target.value)}
                                        >
                                            <MenuItem value="equals">equals</MenuItem>
                                            <MenuItem value="not_equals">not equals</MenuItem>
                                            <MenuItem value="contains">contains</MenuItem>
                                        </Select>
                                    </FormControl>
                                    
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <Select
                                            value={rule.value}
                                            onChange={(e) => handleSkipLogicChange(index, 'value', e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value="">Select value...</MenuItem>
                                            {options.filter(opt => opt.trim()).map((option, optIndex) => (
                                                <MenuItem key={optIndex} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    
                                    <IconButton 
                                        onClick={() => removeSkipLogicRule(index)}
                                        size="small"
                                        color="error"
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormControl size="small" sx={{ minWidth: 80 }}>
                                        <Select
                                            value={rule.action}
                                            onChange={(e) => handleSkipLogicChange(index, 'action', e.target.value)}
                                        >
                                            <MenuItem value="hide">Hide</MenuItem>
                                            <MenuItem value="show">Show</MenuItem>
                                        </Select>
                                    </FormControl>
                                    
                                    <FormControl size="small" sx={{ minWidth: 80 }}>
                                        <Select
                                            value={rule.targetType}
                                            onChange={(e) => handleSkipLogicChange(index, 'targetType', e.target.value)}
                                        >
                                            <MenuItem value="field">Field</MenuItem>
                                            <MenuItem value="section">Section</MenuItem>
                                        </Select>
                                    </FormControl>
                                    
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <Select
                                            value={rule.target}
                                            onChange={(e) => handleSkipLogicChange(index, 'target', e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value="">Select target...</MenuItem>
                                            {getAvailableTargetFields()
                                                .filter(target => target.type === rule.targetType)
                                                .map((target) => (
                                                    <MenuItem key={target.id} value={target.target}>
                                                        {target.label}
                                                    </MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                        ))}
                        
                        <Button 
                            onClick={addSkipLogicRule}
                            size="small" 
                            startIcon={<Add fontSize="small" />}
                            variant="outlined"
                            disabled={options.filter(opt => opt.trim()).length === 0}                        >
                            Add Skip Logic Rule
                        </Button>
                    </AccordionDetails>
                </Accordion>
            </Box>
            );
        
        case 'checkbox':
            return (
                <Box>
                    <TextField
                        label="Group Name"
                        value={groupName}
                        onChange={handleGroupNameChange}
                        fullWidth
                        size="small"
                        sx={{ mb: 1 }}
                        helperText="Checkboxes with same group name will work as radio buttons"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={field.checked || false}
                                onChange={(e) => onUpdate(field.id, { 
                                    checked: e.target.checked,
                                    groupName: groupName // Ensure groupName is included
                                })}
                            />
                        }
                        label="Checked state"
                    />
                </Box>
            );

        case 'radio-group':
            return (
            <TextField
                label="Group Name"
                value={groupName}
                onChange={handleGroupNameChange}
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                helperText="Fields with the same group name will be grouped together"
            />
            );
        
        case 'label':
            return (
            <Typography 
                variant={field.variant || 'h6'} 
                sx={{ 
                mt: 2, 
                mb: 1,
                ...(field.sx || {})
                }}
            >
                {label}
            </Typography>
            );

            case 'date':
                return (
                    <TextField
                        label={field.label || "Date"}
                        type="date"
                        value={field.value || ''}
                        onChange={(e) => onUpdate(field.id, { value: e.target.value })}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        // New MUI v5+ approach only
                        slotProps={{
                            input: {shrink: true,},
                        }}
                    />
                );                case 'file':
                    return (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                                File Upload Configuration
                            </Typography>
                            
                            {/* File Type Selection */}
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Allowed File Types</InputLabel>
                                <Select
                                    value={field.allowedFileTypes || 'all'}
                                    onChange={(e) => onUpdate(field.id, { allowedFileTypes: e.target.value })}
                                    label="Allowed File Types"
                                >
                                    <MenuItem value="all">All Files</MenuItem>
                                    <MenuItem value="images">Images Only (JPG, PNG, GIF, WEBP)</MenuItem>
                                    <MenuItem value="pdf">PDF Only</MenuItem>
                                    <MenuItem value="documents">Documents (PDF, DOC, DOCX)</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Max File Size */}
                            <TextField
                                label="Max File Size (MB)"
                                type="number"
                                value={field.maxFileSize || 5}
                                onChange={(e) => onUpdate(field.id, { maxFileSize: parseInt(e.target.value) })}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                inputProps={{ min: 1, max: 50 }}
                            />

                            {/* Multiple Files Toggle */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.allowMultiple || false}
                                        onChange={(e) => onUpdate(field.id, { allowMultiple: e.target.checked })}
                                    />
                                }
                                label="Allow Multiple Files"
                                sx={{ mb: 2 }}
                            />
                            
                            <Button 
                                variant="outlined" 
                                component="label"
                                sx={{ mb: 1 }}
                            >
                                Choose File (Preview)
                                <input 
                                    type="file" 
                                    hidden 
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            onUpdate(field.id, { 
                                                fileName: file.name,
                                                fileSize: file.size,
                                                fileType: file.type
                                            });
                                        }
                                    }}
                                />
                            </Button>
                            {field.fileName && (                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Selected: {field.fileName} ({formatFileSize(field.fileSize)})
                                </Typography>
                            )}
                        </Box>
                    );

        case 'programme-selector':
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Programme Selector Configuration:
                    </Typography>
                    
                    {/* Max Selections (always present for programme-selector) */}
                    <TextField
                        label="Maximum Selections"
                        type="number"
                        value={field.maxSelections || 1} // Default to 1
                        onChange={(e) => onUpdate(field.id, { maxSelections: parseInt(e.target.value) })}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        inputProps={{ min: 1, max: 10 }}
                        helperText="Maximum number of programmes an applicant can select (using radio buttons)"
                    />

                    {/* Programme Level Filter */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Programme Level</InputLabel>
                        <Select
                            value={field.programmeLevel || 'all'}
                            onChange={(e) => onUpdate(field.id, { programmeLevel: e.target.value })}
                            label="Programme Level"
                        >
                            <MenuItem value="all">All Levels</MenuItem>
                            {availableLevels.map(level => (
                                <MenuItem key={level} value={level.toLowerCase()}>{level}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Faculty Filter */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Faculty Filter</InputLabel>
                        <Select
                            value={field.facultyFilter || 'all'}
                            onChange={(e) => onUpdate(field.id, { facultyFilter: e.target.value })}
                            label="Faculty Filter"
                        >
                            <MenuItem value="all">All Faculties</MenuItem>
                            {availableFaculties.map(faculty => (
                                <MenuItem key={faculty} value={faculty.toLowerCase()}>{faculty}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Display Style */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Display Style</InputLabel>
                        <Select
                            value={field.displayStyle || 'hierarchical'}
                            onChange={(e) => onUpdate(field.id, { displayStyle: e.target.value })}
                            label="Display Style"
                        >
                            <MenuItem value="hierarchical">Hierarchical (Faculty → Department → Programme)</MenuItem>
                            <MenuItem value="flat">Flat List</MenuItem>
                            <MenuItem value="grouped">Grouped by Faculty</MenuItem>
                        </Select>
                    </FormControl>

                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Programmes will be loaded dynamically from the database based on the configured filters.
                    </Typography>
                </Box>
            );
        
        default:
            return null;
        }
    };

    return (
        <Box sx={{ 
        border: '1px solid #ddd', 
        borderRadius: 1, 
        p: 2, 
        mb: 2,
        backgroundColor: '#f9f9f9'
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Tooltip title="Drag And Drop Field">
            <div {...listeners} style={{ cursor: 'grab', marginRight: '8px', color: 'red' }}>
                <DragHandle />
            </div>
            </Tooltip>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {field.type.toUpperCase()} FIELD
            </Typography>
            <Tooltip title="Delete field">
            <IconButton 
                onClick={handleDelete}
                size="small"
                color="error"
                aria-label="delete field"
            >
                <Delete fontSize="small" />
            </IconButton> 
            </Tooltip>
        </Box>

        {field.type !== 'label' && (
            <TextField
            label="Field Label"
            value={label}
            onChange={handleLabelChange}
            fullWidth
            sx={{ mb: 2 }}
            size="small"
            />
        )}

        {field.type === 'label' && (
            <TextField
            label="Field Label"
            value={label}
            onChange={handleLabelChange}
            fullWidth
            sx={{ mb: 2 }}
            size="small"
            />
        )}
        
        {field.type !== 'label' && (
            <FormControlLabel
            control={
                <Checkbox
                checked={field.required || false}
                onChange={handleRequiredChange}
                />
            }
            label="Required"
            sx={{ mb: 2 }}
            />
        )}

        {renderFieldSpecificContent()}
        </Box>
    );
}
